/* eslint-disable @typescript-eslint/no-explicit-any */
import { BrandType } from './brand.types';
import { vDriveDeliveryStatus } from './api/wallet.types';
import { Provider, VDriveProvider } from './shared.types';
import { PosType, ProviderConnectionType } from '.';
import { VDriveCallStatus, VDriveSource } from './deliveries.types';

export type OrderMigrationEntity = {
  id: number;
  currentPage: number;
  brandId: number;
  finished: boolean;
  startedAt: string;
  createdAt: string;
  updatedAt: string;
};

export enum OrderStatus {
  new = 'new',
  accepted = 'accepted',
  completed = 'completed',
  cancelled = 'cancelled',
}

export enum VDriveOrderStatus {
  ASSIGNED = 'assigned',
  CANCELLED = 'cancelled',
  CAN_BE_CANCELLED = 'canBeCancelled',
  NOT_ASSIGNED = 'notAssigned',
  COMPLETED = 'completed',
}

export enum RecoveryStatus {
  RECOVERED = 'recovered',
  ADJUSTMENT = 'adjustment',
  PENDING = 'pending',
  NOT_ELIGIBLE = 'notEligible',
  EVIDENCE_NEEDED = 'evidenceNeeded',
}

export enum DeliveryStatus {
  started = 'started',
  arriving = 'arriving',
  assigned = 'assigned',
  delivered = 'delivered',
  pending = 'pending',
}

export enum DeliveryType {
  restaurant = 'restaurant',
  provider = 'provider',
}
export enum OrderType {
  delivery = 'delivery',
  pickup = 'pickup',
  dinein = 'dinein',
  waiting = 'waiting',
  storein = 'storein',
}

export type VDriveEntity = {
  distanceMiles?: number | null;
  /** What the DSP reported; admin only (always null in the customer app). */
  dspDistanceMiles?: number | null;
  provider?: VDriveProvider | null;
  trackingLink?: string | null;
  driverName?: string | null;
  driverPhoto?: string | null;
  driverPhone?: string | null;
  driverPhoneNumberForMerchant?: string | null;
  driverPhoneNumberForCustomer?: string | null;
  driverPinCodeForMerchant?: string | null;
  cancelledBy?: 'api' | 'partner' | null;
  cancellationReason?: string | null;
  status: VDriveOrderStatus;
  fdStatus: vDriveDeliveryStatus;
  pickupAt?: string | null;
  savings?: number | null;
  source: VDriveSource;
  specialOfferPhone?: {
    isUSNumber: boolean;
    nationalNumber: string;
    number: string;
  } | null;
  interceptorCallId?: string | null;
  interceptorCallResultFailed?: boolean | null;
  interceptorCallStatus?: VDriveCallStatus | null;
  /**
   * Backend guarantee that the call recording endpoint will answer. Only true
   * when both the interceptor call id and the linked call record exist -
   * interceptorCallId alone is not enough and still yields a 400.
   */
  hasCallRecording?: boolean;
};

export type OrderCustomer = {
  name?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  phoneCode?: string | null;
};

export type OrderBrand = {
  name: string;
  isLocation: boolean;
  id: number;
  type: BrandType;
};

export type OrderItemEntity = {
  name: string;
  quantity: number;
  price: number;
  basePrice?: number | null;
  instructions?: string | null;
  preparationNotes?: string | null;
  options?: {
    name: string;
    quantity: number;
    price: number;
    modifierName?: string | null;
    children?: any[];
  }[];
};

export type Order = {
  id: number;
  number: string;
  type: OrderType;
  creationType: 'dp' | 'manual';
  status: OrderStatus;
  notes?: string | null;
  paid: boolean;
  recovered?: number | null;
  recovery?: {
    amount: number;
    status: RecoveryStatus;
    notes?: string | null;
  } | null;
  asap: boolean;
  timeZone: string;
  addDisposableItems: boolean;
  prepTimeMinutes?: number | null;
  provider: Provider;
  // Who actually delivers when delivery.type is provider. Never masked to
  // vGrubs like `provider` is on our virtual brands; null on restaurant delivery.
  deliveryProvider?: Provider | null;
  paymentMethod: string;
  date: string;
  formattedDeliveryAddress?: string | null;

  delivery?: {
    type?: DeliveryType | null;
    status?: DeliveryStatus | null;
    notes?: string | null;
    courier?: {
      name?: string | null;
      status?: 'pending' | 'assigned' | null;
      phoneNumber?: string | null;
      photoUrl?: string | null;
    } | null;
    address?: {
      address?: string | null;
      city?: string | null;
      state?: string | null;
      country?: string | null;
      zipCode?: string | null;
      unitNumber?: string | null;
    } | null;
  } | null;

  items: OrderItemEntity[];

  charges: OrderCharges | null;

  customer?: {
    name?: string | null;
    email?: string | null;
    phoneNumber?: string | null;
    phoneCode?: string | null;
  } | null;

  timestamps?: {
    placedAt: string;
    createAt: string;
    acceptedAt?: string | null;
    cancelledAt?: string | null;
    completedAt?: string | null;
    pickupAt?: string | null;
    deliveryAt?: string | null;
    scheduledFor?: string | null;
    createdAt: string;
  } | null;

  brand: OrderBrand;

  location: {
    name: string;
    id: number;
  };

  vDriveProvider?: VDriveProvider | null;
  vDriveEnabled?: boolean | null;

  vDrive?: VDriveEntity;
  roomName?: string | null;
  tableName?: string | null;
  fleksaSource?: 'QR' | 'WEBSITE' | 'POS' | null;
  cancellationReason?: string | null;
  source: ProviderConnectionType;
  rejectReason?: string | null;
  posPartnerId?: string | null;
  posType?: PosType | null;
  posInjectionStatus?: PosInjectionStatus | null;
};

export type OrderCharges = {
  tax: number | null;
  tips: number | null;
  deliveryFee: number | null;
  serviceFee: number | null;
  restaurantTips: number | null;
  subTotal: number | null;
  discount: number | null;
  total: number | null;
  additionalCharges: OrderAdditionalCharges[];
};

export type OrderAdditionalCharges = {
  info: string;
  amount: number;
  label: string;
};

export type SimpleOrderEntity = Pick<
  Order,
  | 'id'
  | 'number'
  | 'provider'
  | 'deliveryProvider'
  | 'paid'
  | 'recovered'
  | 'recovery'
  | 'status'
  | 'timeZone'
  | 'type'
  | 'date'
  | 'vDriveProvider'
  | 'vDrive'
  | 'roomName'
  | 'tableName'
  | 'fleksaSource'
  | 'rejectReason'
  | 'creationType'
  | 'posPartnerId'
> & {
  pickupAt?: string | null;
  scheduledFor?: string | null;
  isScheduled: boolean;
  deliveryType?: DeliveryType | null;
  brandName: string;
  isFromLocation: boolean;
  customerName?: string | null;
  totalPrice?: number | null;
  subtotalPrice?: number | null;
  storeName: string;
  source: VDriveSource;
  locationId: number;
  brandId: number;
  posPartnerId?: string | null;
  posInjectionStatus?: PosInjectionStatus | null;
  posType?: PosType | null;
};

export enum AuditLogOrderType {
  handled = 'handled',
  moneyReturned = 'moneyReturned',
  webhookSent = 'webhookSent',
  webhookReceived = 'webhookReceived',
  eventReceived = 'eventReceived',
  eventFinished = 'eventFinished',
  manualAccepted = 'manualAccepted',
  autoAccepted = 'autoAccepted',
}

export type OrderAuditLogEntity = {
  id: number;
  createdAt: string;
  info: string;
  type: AuditLogOrderType;
  orderId: number;
};

export type VDriveOfferEntity = {
  id: number;
  price: number;
  provider: VDriveProvider;
  distance: number;
  deliveryMinutes: number;
  pickupMinutes: number;
  tip?: number | null;
};

export type PosTypeWithId = {
  id: number;
  posType: PosType;
};

export enum PosInjectionStatus {
  INJECTED = 'injected',
  REJECTED = 'rejected',
}
